import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { Link } from "wouter";
import SEOHead from "@/components/SEOHead";
import SearchHeader from "@/components/search-header";
import Footer from "@/components/footer";
import { loadAllArtworks, searchArtworks, getRandomArtwork, StaticArtwork } from "@/lib/static-data";
import { trackEvent } from "@/lib/analytics";
import EmotionMiniBars from "@/components/emotion-mini-bars";
import type { EmotionScore } from "@shared/hubs";

interface ThemeArtwork {
  id: number;
  title: string;
  artist: string;
  imageUrl?: string;
  score: number;
  emotions?: EmotionScore[];
}

interface ThemeData {
  emotion: string;
  slug: string;
  title: string;
  description: string;
  intro: string;
  artworks: ThemeArtwork[];
}

// Themes chosen by subject keep their own colours.
const THEME_COLORS: Record<string, string> = {
  eerie: "from-gray-800 to-purple-900",
};

const EMOTION_COLORS: Record<string, string> = {
  Wonder: "from-indigo-500 to-purple-600",
  Joy: "from-yellow-400 to-orange-500",
  Sadness: "from-blue-500 to-slate-600",
  Nostalgia: "from-amber-500 to-brown-600",
  Tension: "from-red-500 to-rose-700",
  Arousal: "from-orange-500 to-red-600",
  Valence: "from-emerald-400 to-teal-600",
  Anger: "from-red-600 to-red-900",
  Dominance: "from-slate-600 to-gray-900",
  Interest: "from-cyan-500 to-blue-600",
};

export default function EmotionTheme() {
  const params = useParams<{ theme: string }>();
  const [, setLocation] = useLocation();
  const [themeData, setThemeData] = useState<ThemeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comprehensiveArtworks, setComprehensiveArtworks] = useState<StaticArtwork[]>([]);
  const [searchResults, setSearchResults] = useState<StaticArtwork[]>([]);

  const slug = params.theme;

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const [themeResp, artworks] = await Promise.all([
          fetch(`/seo-themes/${slug}.json`),
          loadAllArtworks(),
        ]);

        if (!themeResp.ok) {
          setError("Theme not found");
          return;
        }

        const data: ThemeData = await themeResp.json();
        setThemeData(data);
        setComprehensiveArtworks(artworks);
      } catch (err) {
        setError("Failed to load theme data");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [slug]);

  const handleArtworkSelect = (artworkId: number) => {
    setLocation(`/artwork/${artworkId}/`);
    trackEvent("view_artwork", "navigation", `theme_${slug}`, artworkId);
  };

  const handleSearch = (query: string) => {
    if (!query.trim()) { setSearchResults([]); return; }
    const results = searchArtworks(comprehensiveArtworks, query, 20);
    setSearchResults(results);
    trackEvent("search", "artwork_search", query, results.length);
  };

  const handleRandomArtwork = () => {
    const randomArtwork = getRandomArtwork(comprehensiveArtworks);
    if (randomArtwork?.id) {
      setLocation(`/artwork/${randomArtwork.id}/`);
      trackEvent("view_artwork", "navigation", "random_button", randomArtwork.id);
    }
  };

  const gradientClass = themeData
    ? (THEME_COLORS[themeData.slug] ?? EMOTION_COLORS[themeData.emotion] ?? "from-gray-600 to-gray-800")
    : "";

  return (
    <div className="min-h-screen bg-background">
      {themeData && (
        <SEOHead
          title={`${themeData.title} — Ariadne's Gallery`}
          description={themeData.description}
          url={`https://ariadnesgallery.com/emotion/${slug}/`}
          type="website"
        />
      )}

      <SearchHeader
        artworks={searchResults}
        onArtworkSelect={handleArtworkSelect}
        onSearch={handleSearch}
        totalCount={comprehensiveArtworks.length}
        onRandomArtwork={handleRandomArtwork}
      />

      <main className="pt-16">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-muted-foreground">Loading...</div>
          </div>
        )}

        {error && (
          <div className="max-w-4xl mx-auto px-4 py-12 text-center">
            <p className="text-muted-foreground">{error}</p>
            <Link href="/" className="mt-4 inline-block text-indigo-600 hover:underline">
              Return to gallery
            </Link>
          </div>
        )}

        {themeData && !loading && (
          <>
            {/* Hero */}
            <div className={`bg-gradient-to-r ${gradientClass} text-white py-16`}>
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-sm uppercase tracking-widest opacity-75 mb-3">
                  Emotion Collection
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold mb-6">{themeData.title}</h1>
                <p className="text-lg opacity-90 max-w-2xl">{themeData.intro}</p>
                <div className="mt-6 flex gap-4">
                  <Link
                    href="/emotion-search"
                    className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Find art by emotion →
                  </Link>
                </div>
              </div>
            </div>

            {/* Artwork grid */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <p className="text-sm text-muted-foreground mb-8">
                {themeData.artworks.length} artworks · {THEME_COLORS[themeData.slug] ? `chosen by subject and ${themeData.emotion.toLowerCase()} score` : `ranked by ${themeData.emotion.toLowerCase()} score`}
              </p>

              <div className="grid gap-3">
                {themeData.artworks.map((artwork, index) => (
                  <button
                    key={artwork.id}
                    onClick={() => handleArtworkSelect(artwork.id)}
                    className="flex items-center gap-4 p-3 bg-white border rounded-lg hover:border-indigo-300 hover:shadow-sm transition-all text-left group w-full"
                  >
                    <span className="text-lg font-bold text-muted-foreground/30 w-8 shrink-0 text-right">
                      {index + 1}
                    </span>
                    {artwork.imageUrl ? (
                      <img
                        src={artwork.imageUrl}
                        alt={artwork.title}
                        className="w-12 h-12 object-cover rounded shrink-0 bg-gray-100"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded shrink-0 bg-gray-100" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium group-hover:text-indigo-700 transition-colors truncate">
                        {artwork.title}
                      </div>
                      <div className="text-sm text-muted-foreground truncate">
                        {artwork.artist}
                      </div>
                      <EmotionMiniBars emotions={artwork.emotions} className="sm:hidden mt-1" />
                    </div>
                    <EmotionMiniBars emotions={artwork.emotions} className="hidden sm:block w-48 shrink-0" />
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-semibold text-indigo-600">{artwork.score}</div>
                      <div className="text-xs text-muted-foreground">{themeData.emotion}</div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-12 text-center">
                <p className="text-muted-foreground mb-4">
                  Explore art by your own emotional profile
                </p>
                <Link
                  href="/emotion-search"
                  className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Open Emotion Search
                </Link>
              </div>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
